import React from 'react';

/**
 * Thin top-of-page banner that communicates SSE connection state.
 *
 * Renders nothing on the happy path; scales alarm with how bad / how long
 * the disconnect has been:
 *
 *   offline      → blue   "You're offline. Changes will sync when you reconnect."
 *   lost-long    → red    "Can't reach server." + manual Retry button
 *   lost         → orange "Connection lost. Reconnecting…"
 *   reconnecting → yellow "Reconnecting…"
 *   back-online  → green  "Back online"  (1s fade after recovery)
 *   connected    → null
 *
 * The parent (App.tsx) computes the status from its connection-state signals
 * (chatConnected, chatErrorCount, disconnectedAt, isOnline, backOnlineUntil).
 */

export type ConnectionStatus =
  | 'connected'
  | 'reconnecting'
  | 'lost'
  | 'lost-long'
  | 'offline'
  | 'back-online';

interface Props {
  status: ConnectionStatus;
  onManualReconnect?: () => void;
}

const STYLES: Record<Exclude<ConnectionStatus, 'connected'>, { bg: string; fg: string; label: string }> = {
  'reconnecting': { bg: '#fef3c7', fg: '#92400e', label: 'Reconnecting…' },
  'lost':         { bg: '#fed7aa', fg: '#9a3412', label: 'Connection lost. Reconnecting…' },
  'lost-long':    { bg: '#fecaca', fg: '#991b1b', label: "Can't reach server." },
  'offline':      { bg: '#dbeafe', fg: '#1e40af', label: "You're offline. Changes will sync when you reconnect." },
  'back-online':  { bg: '#d1fae5', fg: '#065f46', label: 'Back online' },
};

const ConnectionBanner: React.FC<Props> = ({ status, onManualReconnect }) => {
  if (status === 'connected') return null;
  const s = STYLES[status];
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: s.bg,
        color: s.fg,
        padding: '6px 16px',
        fontSize: 'var(--fs-sm)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'opacity 0.3s ease',
      }}
    >
      <span>{s.label}</span>
      {status === 'lost-long' && onManualReconnect && (
        <button
          onClick={onManualReconnect}
          style={{
            background: 'transparent',
            border: `1px solid ${s.fg}`,
            color: s.fg,
            padding: '2px 10px',
            fontSize: 'var(--fs-xs)',
            borderRadius: 4,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
};

export default ConnectionBanner;
